import TurndownService from "turndown";

export function formatMarkdownToHtml(markdown) {
    if (!markdown) return "";

    // Remove all leading empty lines and whitespace at the start
    markdown = markdown.replace(/^[\s\r\n]+/, '');
    const style = 'background-color: rgb(255, 255, 255); color: rgb(0, 0, 0);';
    const qlUiSpan = '<span class="ql-ui" contenteditable="false"></span>';

    // Normalize line endings to \n
    markdown = markdown.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

    // Clean over-escaped characters in URLs
    markdown = markdown.replace(/\\\[/g, '[').replace(/\\\]/g, ']');
    // Clean escaped asterisks that shouldn't be escaped
    markdown = markdown.replace(/\\\*/g, '*');
    markdown = markdown.replace(/\\\\+/g, '\\');

    // Mention markdown to HTML span
    let html = markdown.replace(/\[([^\]]+)\]\(\/community\/profiles\/(\d+)\)/g, (match, value, id) => {
        return `<span class="mention" data-index="0" data-denotation-char="@" data-id="${id}" data-value="${value.replace(/^@/, '')}"><span contenteditable="false"><span class="ql-mention-denotation-char">@</span><span class="ql-mention-value">${value.replace(/^@/, '')}</span></span></span>`;
    });

    // converting markdown links to HTML and temporarily replace them with placeholders
    let linkPlaceholders = [];
    let linkIndex = 0;
    // html = markdown.replace(/__\[(.*?)\]\((.*?)\)__/g, (match, text, url) => {
    //     const tag = `<u style="${style}"><a href="${url}" target="_blank" rel="noopener noreferrer">${text}</a></u>`;
    //     linkPlaceholders.push(tag);
    //     return `<<LINKPLACEHOLDER${linkIndex++}>>`;
    // });
    html = html.replace(/\[([^\]]+)\]\((https:\/\/[^\)]+)\)/g, (match, text, url) => {
        const tag = `<a href="${url}" target="_blank" style="color: #775931;text-decoration:underline" rel="noopener noreferrer">${text}</a>`;
        linkPlaceholders.push(tag);
        return `<<LINKPLACEHOLDER${linkIndex++}>>`;
    });

    // Applying formatting replacements only to non-link text
    // Combined formatting
    html = html.replace(/\*\*__([^*]+?)__\*\*/g, `<strong style="${style}"><u>$1</u></strong>`);
    html = html.replace(/\*__([^*]+?)__\*/g, `<em style="${style}"><u>$1</u></em>`);
    html = html.replace(/__\*\*([^_]+?)\*\*__/g, `<u><strong style="${style}">$1</strong></u>`);
    html = html.replace(/__\*([^_]+?)\*__/g, `<u><em style="${style}">$1</em></u>`);

    // Individual styles
    html = html.replace(/__([^_]+?)__/g, `<u style="${style}">$1</u>`);
    html = html.replace(/\*\*([^*]+?)\*\*/g, `<strong style="${style}">$1</strong>`);
    html = html.replace(/_([^_]+?)_/g, `<em style="${style}">$1</em>`);

    // Strikethrough
    html = html.replace(/~~([^~]+?)~~/g, `<s style="${style}">$1</s>`);

    // Restore link placeholders
    html = html.replace(/<<LINKPLACEHOLDER(\d+)>>/g, (_, idx) => linkPlaceholders[Number(idx)]);

    // Unordered list items
    html = html.replace(/^-\s+(.*)$/gm, (_, itemText) =>
        `<li data-list="bullet">${qlUiSpan}<span style="${style}">${itemText}</span></li>`
    );

    // Ordered list items
    html = html.replace(/^\d+\.\s+(.*)$/gm, (_, itemText) =>
        `<li data-list="ordered">${qlUiSpan}<span style="${style}">${itemText}</span></li>`
    );

    // Wrap bullet list blocks in <ol>
    html = html.replace(/((<li data-list="bullet">[\s\S]*?<\/li>\s*)+)/g, match => {
        return `<ol>${match.trim().replace(/\n/g, '')}</ol>`;
    });

    // Wrap ordered list blocks in <ol>
    html = html.replace(/((<li data-list="ordered">[\s\S]*?<\/li>\s*)+)/g, match => {
        return `<ol>${match.trim().replace(/\n/g, '')}</ol>`;
    });

    // Split by single newlines and wrap each line in a paragraph
    // This preserves the line break structure from your markdown
    const lines = html.split('\n');

    html = lines.map(line => {
        // Don't wrap list items in paragraphs (they're already wrapped in <ol>)
        if (line.trim().startsWith('<ol>') || line.trim().startsWith('</ol>')) {
            return line;
        }
        if (line.trim() === '') {
            return `<br>`;
        }
        return `<p><span style="${style}">${line}</span></p>`;
    }).join('');

    // Remove empty <p> tags
    html = html.replace(/<p>\s*<\/p>/g, '');

    return html;
}

export const createTurndown = () => {
    const turndownService = new TurndownService({
        headingStyle: "atx",
        codeBlockStyle: "fenced",
        emDelimiter: "_",      // match your italic _
        strongDelimiter: "**"  // match your bold **
    });

    /*
     * LINK
     * <a href="url">text</a>
     * → [text](url)
     */
    turndownService.addRule("links", {
        filter: "a",
        replacement: function (content, node) {
            const href = node.getAttribute("href");
            return `[${content}](${href})`;
        }
    });

    /*
     * UNDERLINE
     * <u>text</u>
     * → __text__
     */
    turndownService.addRule("underline", {
        filter: ["u"],
        replacement: function (content) {
            return `__${content}__`;
        }
    });

    /*
     * STRIKETHROUGH
     * <s>text</s>
     * → ~~text~~
     */
    turndownService.addRule("strike", {
        filter: ["s", "del"],
        replacement: function (content) {
            return `~~${content}~~`;
        }
    });

    // Remove empty Quill UI spans
    turndownService.addRule("removeQuillUI", {
        filter: (node) =>
            node.classList && node.classList.contains('ql-ui'),
        replacement: () => ''
    });

    // Rule for unordered lists
    turndownService.addRule("quillUnorderedList", {
        filter: (node) =>
            node.nodeName === "LI" && node.getAttribute("data-list") === "bullet",
        replacement: (content) => {
            content = content.replace(/^\s*$\n?/gm, '').trim();
            return `- ${content}\n`;
        }
    });

    // Rule for ordered lists
    turndownService.addRule("quillOrderedList", {
        filter: (node) =>
            node.nodeName === "LI" && node.getAttribute("data-list") === "ordered",
        replacement: function (content, node) {
            // Get all ordered list items that come before this one
            const allItems = Array.from(node.parentNode.children);
            const orderedItemsBeforeThis = allItems
                .slice(0, allItems.indexOf(node))
                .filter(item => item.getAttribute("data-list") === "ordered");

            // Calculate the correct index based only on ordered items
            const index = orderedItemsBeforeThis?.length + 1;

            // Clean up the content
            content = content.replace(/^\s*$\n?/gm, '').trim();

            return `${index}. ${content}\n`;
        }
    });

    /*
     * BOLD + UNDERLINE + ITALIC
     * <strong><u><em>text</em></u></strong>
     * → ___text___
     */
    turndownService.addRule("boldUnderlineItalic", {
        filter: function (node) {
            return (
                node.nodeName === "STRONG" &&
                node.firstChild &&
                node.firstChild.nodeName === "U" &&
                node.firstChild.firstChild &&
                node.firstChild.firstChild.nodeName === "EM"
            );
        },
        replacement: function (content) {
            return `___${content}___`;
        }
    });

    // turndownService.addRule("boldUnderlineItalic", {
    //     filter: function (node) {
    //         if (node.nodeName !== "STRONG") return false;
    //
    //         const hasEm = node.querySelector("em");
    //         const hasU = node.querySelector("u");
    //
    //         return hasEm && hasU;
    //     },
    //     replacement: function (content) {
    //         return `___${content}___`;
    //     }
    // });

    turndownService.addRule("paragraph", {
        filter: "p",
        replacement: function (content) {
            return content + "\n";
        }
    });
    /*
     * LINE BREAKS
     * <br> or <br/>
     * → \n
     */
    turndownService.addRule("lineBreak", {
        filter: "br",
        replacement: function () {
            return "\n";
        }
    });

    return turndownService;
};